import {inject, Injectable, signal} from '@angular/core';
import {MypartHttpService} from './mypart-http.service';

/**
 * Interface for HTTP Error Modal data
 */
export interface HttpErrorModalData {
  errorMessage: string;
  errorDetails?: string;
  errorCode?: string;
}

/**
 * Interface for No Matches Modal data
 */
export interface NoMatchesModalData {
  message?: string;
}

/**
 * Interface for Regular Error Message Modal data
 */
export interface ErrorMessageModalData {
  message: string;
}

/**
 * Modal Service - Manages all modals in the application using Angular 19 signals
 */
@Injectable({
  providedIn: 'root'
})
export class ModalService {
  private mypartHttpService = inject(MypartHttpService);
  // HTTP Error Modal state
  private _showHttpErrorModal = signal<boolean>(false);
  // Public read-only signals
  public readonly showHttpErrorModal = this._showHttpErrorModal.asReadonly();
  private _httpErrorData = signal<HttpErrorModalData | null>(null);
  public readonly httpErrorData = this._httpErrorData.asReadonly();
  private _showErrorDetails = signal<boolean>(false);
  public readonly showErrorDetails = this._showErrorDetails.asReadonly();
  // No Matches Modal state
  private _showNoMatchesModal = signal<boolean>(false);
  public readonly showNoMatchesModal = this._showNoMatchesModal.asReadonly();
  private _noMatchesData = signal<NoMatchesModalData | null>(null);
  public readonly noMatchesData = this._noMatchesData.asReadonly();
  // Regular Error Message Modal state
  private _showErrorMessageModal = signal<boolean>(false);
  public readonly showErrorMessageModal = this._showErrorMessageModal.asReadonly();
  private _errorMessageData = signal<ErrorMessageModalData | null>(null);
  public readonly errorMessageData = this._errorMessageData.asReadonly();
  // Lead Gen Modal state
  private _showLeadGenModal = signal<boolean>(false);
  public readonly showLeadGenModal = this._showLeadGenModal.asReadonly();

  constructor() {
  }

  /**
   * Handles HTTP errors with login and retry flow
   * First attempts a background login, then allows for retry of the original operation
   * Only shows the HTTP error modal if explicitly requested
   *
   * @param data HTTP Error Modal data
   * @param showModal Whether to immediately show the error modal
   * @returns Promise that resolves when login is complete
   */
  handleHttpError(data: HttpErrorModalData, showModal: boolean = false): Promise<void> {
    // Store the error data in case we need to show the modal later
    this._httpErrorData.set(data);

    // Only show the modal if explicitly requested
    if (showModal) {
      this._showHttpErrorModal.set(true);
      this._showErrorDetails.set(false);
    }

    console.log('Attempting background login due to HTTP error...');
    return this.attemptBackgroundLogin();
  }

  /**
   * Shows the HTTP Error Modal with the provided data
   * @param data HTTP Error Modal data
   */
  showHttpError(data: HttpErrorModalData): void {
    this._httpErrorData.set(data);
    this._showHttpErrorModal.set(true);
    this._showErrorDetails.set(false);
  }

  /**
   * Closes the HTTP Error Modal
   */
  closeHttpErrorModal(): void {
    this._showHttpErrorModal.set(false);
    this._httpErrorData.set(null);
  }

  /**
   * Toggles the visibility of error details in the HTTP Error Modal
   */
  toggleErrorDetails(): void {
    this._showErrorDetails.update(value => !value);
  }

  /**
   * Shows the No Matches Modal with the provided data
   * @param data No Matches Modal data
   */
  showNoMatches(data?: NoMatchesModalData): void {
    this._noMatchesData.set(data || {});
    this._showNoMatchesModal.set(true);
  }

  /**
   * Closes the No Matches Modal
   */
  closeNoMatchesModal(): void {
    this._showNoMatchesModal.set(false);
    this._noMatchesData.set(null);
  }

  /**
   * Shows the Error Message Modal with the provided data
   * @param data Error Message Modal data
   */
  showErrorMessage(data: ErrorMessageModalData): void {
    this._errorMessageData.set(data);
    this._showErrorMessageModal.set(true);
  }

  /**
   * Closes the Error Message Modal
   */
  closeErrorMessageModal(): void {
    this._showErrorMessageModal.set(false);
    this._errorMessageData.set(null);
  }

  /**
   * Shows the Lead Gen Modal
   */
  showLeadGen(): void {
    this._showLeadGenModal.set(true);
  }

  /**
   * Closes the Lead Gen Modal
   */
  closeLeadGenModal(): void {
    this._showLeadGenModal.set(false);
  }

  /**
   * Attempts a background login and returns a Promise that resolves when login is complete
   * @returns Promise that resolves when login is complete
   */
  private attemptBackgroundLogin(): Promise<void> {
    return new Promise<void>((resolve) => {
      // Create a temporary subscription to the login response
      const loginSub = this.mypartHttpService.login().subscribe({
        next: (response: any) => {
          console.log('Background login successful');
          resolve();
          loginSub.unsubscribe();
        },
        error: (error: any) => {
          console.error('Background login failed:', error);
          resolve(); // Resolve anyway to continue the flow
          loginSub.unsubscribe();
        }
      });
    });
  }
}
